import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Sparkles, Info, Pencil, Minus, Plus, X } from 'lucide-react';

/**
 * Transparently shows how Nanouk estimated nutrition values when the user
 * didn't give exact portions — AND lets the user inline-edit every value
 * (portion, kcal, protein, carbs, fat) once expanded. Totals recompute live
 * and bubble up via `onChange(updatedResult)`. Collapsed by default to keep
 * the main UI lean.
 *
 * Expects an `analyzeFood` response shape:
 *   { breakdown: [{name, portion, calories, protein, carbs, fat}], assumptions, tip, confidence,
 *     calories, protein, carbs, fat }
 */
export default function NanoukEstimateBreakdown({ result, language = 'de', defaultOpen = false, onChange }) {
    const [open, setOpen] = useState(defaultOpen);
    // Tap-to-explain sheet: which macro (kcal | protein | carbs | fat) the user tapped on the TOTAL row
    const [explainMacro, setExplainMacro] = useState(null);

    const parseNum = useCallback((v) => {
        if (v == null) return 0;
        const s = String(v).replace(',', '.').replace(/[^\d.\-]/g, '');
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
    }, []);

    const recomputeTotals = useCallback((items) => {
        return items.reduce(
            (acc, it) => ({
                calories: acc.calories + parseNum(it.calories),
                protein: acc.protein + parseNum(it.protein),
                carbs: acc.carbs + parseNum(it.carbs),
                fat: acc.fat + parseNum(it.fat),
            }),
            { calories: 0, protein: 0, carbs: 0, fat: 0 },
        );
    }, [parseNum]);

    const updateItemField = (idx, field, newValue) => {
        if (!result || !Array.isArray(result.breakdown)) return;
        const updatedBreakdown = result.breakdown.map((it, i) => {
            if (i !== idx) return it;
            const value = field === 'portion' ? newValue : parseNum(newValue);
            return { ...it, [field]: value };
        });
        const totals = recomputeTotals(updatedBreakdown);
        const updated = {
            ...result,
            breakdown: updatedBreakdown,
            calories: Math.round(totals.calories),
            protein: Math.round(totals.protein * 10) / 10,
            carbs: Math.round(totals.carbs * 10) / 10,
            fat: Math.round(totals.fat * 10) / 10,
            // mark as user-edited so caller knows totals were manually adjusted
            edited: true,
        };
        onChange?.(updated);
    };

    // Extract a numeric gram value from a portion string ("200 g", "125g", "10 g (1 EL)")
    const extractGrams = useCallback((portion) => {
        if (!portion) return null;
        const m = String(portion).match(/(\d+(?:[.,]\d+)?)\s*g\b/i);
        if (!m) return null;
        const n = parseFloat(m[1].replace(',', '.'));
        return Number.isFinite(n) && n > 0 ? n : null;
    }, []);

    // Smart step size: smaller for small portions, larger for big ones
    const stepFor = (grams) => {
        if (grams < 30) return 5;
        if (grams < 100) return 10;
        if (grams < 300) return 25;
        return 50;
    };

    // Step the portion up/down AND scale all macros proportionally
    const stepPortion = (idx, direction) => {
        if (!result || !Array.isArray(result.breakdown)) return;
        const item = result.breakdown[idx];
        const currentG = extractGrams(item.portion);
        if (currentG == null) return;
        const step = stepFor(currentG);
        const newG = Math.max(step, Math.round((currentG + direction * step) / step) * step);
        if (newG === currentG) return;
        const ratio = newG / currentG;
        // Preserve any suffix after the gram value (e.g., " (1 EL)")
        const newPortion = String(item.portion).replace(/(\d+(?:[.,]\d+)?)\s*g\b/i, `${newG} g`);
        const updatedBreakdown = result.breakdown.map((it, i) => {
            if (i !== idx) return it;
            return {
                ...it,
                portion: newPortion,
                calories: Math.round(parseNum(it.calories) * ratio),
                protein: Math.round(parseNum(it.protein) * ratio * 10) / 10,
                carbs: Math.round(parseNum(it.carbs) * ratio * 10) / 10,
                fat: Math.round(parseNum(it.fat) * ratio * 10) / 10,
            };
        });
        const totals = recomputeTotals(updatedBreakdown);
        onChange?.({
            ...result,
            breakdown: updatedBreakdown,
            calories: Math.round(totals.calories),
            protein: Math.round(totals.protein * 10) / 10,
            carbs: Math.round(totals.carbs * 10) / 10,
            fat: Math.round(totals.fat * 10) / 10,
            edited: true,
        });
    };

    if (!result) return null;
    const hasBreakdown = Array.isArray(result.breakdown) && result.breakdown.length > 0;
    const hasAssumptions = !!(result.assumptions && String(result.assumptions).trim());
    const hasTip = !!(result.tip && String(result.tip).trim());
    if (!hasBreakdown && !hasAssumptions && !hasTip) return null;

    const editable = open && hasBreakdown && typeof onChange === 'function';

    const t = {
        title: language === 'de' ? 'Wie Nanouk geschätzt hat' : 'How Nanouk estimated',
        subtitleClosed: language === 'de' ? 'Tippe für Details — Werte bleiben anpassbar' : 'Tap for details — values stay editable',
        subtitleOpen: language === 'de' ? 'Werte direkt anpassen → Total aktualisiert sich' : 'Edit any value → total updates live',
        ingredient: language === 'de' ? 'Zutat' : 'Ingredient',
        portion: language === 'de' ? 'Menge' : 'Portion',
        kcal: 'kcal',
        protein: language === 'de' ? 'Eiweiß' : 'Protein',
        carbs: language === 'de' ? 'Kohlenh.' : 'Carbs',
        fat: language === 'de' ? 'Fett' : 'Fat',
        total: language === 'de' ? 'GESAMT' : 'TOTAL',
        portionsLabel: language === 'de' ? 'Angenommene Portionen' : 'Assumed portions',
        highlightLabel: language === 'de' ? 'Nährstoff-Highlight' : 'Nutrient highlight',
        editedBadge: language === 'de' ? 'angepasst' : 'edited',
        editHint: language === 'de'
            ? 'Tippe auf einen Wert, um ihn anzupassen. Mit −/+ wird die Menge in g skaliert (Makros folgen).'
            : 'Tap any value to adjust. The −/+ buttons scale the portion in g (macros follow).',
        tapHint: language === 'de' ? 'Tippe für Aufschlüsselung' : 'Tap for breakdown',
        explainKcalTitle: language === 'de' ? 'Wie kommen die kcal zustande?' : 'How do the calories add up?',
        explainProteinTitle: language === 'de' ? 'Woher kommt das Protein?' : 'Where does the protein come from?',
        explainCarbsTitle: language === 'de' ? 'Woher kommen die Kohlenhydrate?' : 'Where do the carbs come from?',
        explainFatTitle: language === 'de' ? 'Woher kommt das Fett?' : 'Where does the fat come from?',
        contribution: language === 'de' ? 'Anteil' : 'Share',
        close: language === 'de' ? 'Schliessen' : 'Close',
        confidenceLabels: {
            high: language === 'de' ? 'hohe Konfidenz' : 'high confidence',
            medium: language === 'de' ? 'mittlere Konfidenz' : 'medium confidence',
            low: language === 'de' ? 'niedrige Konfidenz' : 'low confidence',
        },
    };

    const totalsDisplay = {
        calories: Math.round(result.calories || 0),
        protein: Number(result.protein || 0).toFixed(1),
        carbs: Number(result.carbs || 0).toFixed(1),
        fat: Number(result.fat || 0).toFixed(1),
    };

    // Reusable inline-edit cell
    const InlineCell = ({ value, idx, field, suffix = '', align = 'right', colorClass = '', widthClass = 'w-12', typeNumber = true }) => {
        const display = typeNumber ? (value == null ? '' : Number(value).toString()) : (value ?? '');
        return (
            <div className={`flex items-center ${align === 'right' ? 'justify-end' : 'justify-start'} gap-0.5`}>
                <input
                    type={typeNumber ? 'text' : 'text'}
                    inputMode={typeNumber ? 'decimal' : 'text'}
                    value={display}
                    onChange={(e) => updateItemField(idx, field, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`${field} for row ${idx + 1}`}
                    data-testid={`breakdown-edit-${field}-${idx}`}
                    className={`${widthClass} bg-transparent border-b border-dashed border-transparent hover:border-amber-400 focus:border-amber-500 focus:bg-amber-50 dark:focus:bg-amber-900/20 outline-none px-0.5 py-0.5 text-xs ${align === 'right' ? 'text-right' : 'text-left'} ${colorClass} transition-colors`}
                />
                {suffix && <span className={`text-[10px] ${colorClass} opacity-70`}>{suffix}</span>}
            </div>
        );
    };

    return (
        <div className="space-y-2" data-testid="nanouk-estimate-breakdown">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid="nanouk-estimate-toggle"
                className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800/50 hover:brightness-[1.03] transition-all text-left"
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-300" />
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium text-sm text-amber-900 dark:text-amber-100 flex items-center gap-2 flex-wrap">
                            🐺 {t.title}
                            {result.confidence && t.confidenceLabels[result.confidence] && (
                                <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200">
                                    {t.confidenceLabels[result.confidence]}
                                </span>
                            )}
                            {result.edited && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                                    ✓ {t.editedBadge}
                                </span>
                            )}
                        </p>
                        <p className="text-xs text-amber-700/80 dark:text-amber-200/70 truncate">
                            {open ? t.subtitleOpen : t.subtitleClosed}
                        </p>
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-amber-600 dark:text-amber-300 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-3 pt-1">
                            {/* Assumptions */}
                            {hasAssumptions && (
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-800/40">
                                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                                        <span className="font-semibold">{t.portionsLabel}: </span>
                                        {result.assumptions}
                                    </p>
                                </div>
                            )}

                            {/* Edit hint */}
                            {editable && (
                                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/30">
                                    <Pencil className="w-3 h-3 text-amber-600 dark:text-amber-300 flex-shrink-0" />
                                    <p className="text-[11px] text-amber-700 dark:text-amber-200/90 leading-tight">
                                        {t.editHint}
                                    </p>
                                </div>
                            )}

                            {/* Breakdown — mobile-first card layout per ingredient */}
                            {hasBreakdown && (
                                <div className="rounded-lg border border-border overflow-hidden bg-card">
                                    {result.breakdown.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="p-2.5 border-t border-border first:border-t-0 space-y-1.5"
                                        >
                                            {/* Row 1: ingredient name */}
                                            <div className="font-medium text-xs text-foreground truncate" title={item.name}>
                                                {item.name}
                                            </div>

                                            {/* Row 2: portion controls */}
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">{t.portion}:</span>
                                                {editable && extractGrams(item.portion) != null && (
                                                    <button
                                                        type="button"
                                                        onClick={() => stepPortion(idx, -1)}
                                                        aria-label={`decrease portion of ${item.name}`}
                                                        data-testid={`breakdown-step-down-${idx}`}
                                                        className="w-6 h-6 flex items-center justify-center rounded border border-amber-300 dark:border-amber-700 bg-white/70 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-200 transition-colors flex-shrink-0"
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </button>
                                                )}
                                                {editable ? (
                                                    <InlineCell
                                                        value={item.portion || ''}
                                                        idx={idx}
                                                        field="portion"
                                                        align="left"
                                                        widthClass="flex-1 min-w-0"
                                                        typeNumber={false}
                                                        colorClass="text-foreground"
                                                    />
                                                ) : (
                                                    <span className="flex-1 min-w-0 truncate text-xs text-muted-foreground" title={item.portion}>{item.portion || '—'}</span>
                                                )}
                                                {editable && extractGrams(item.portion) != null && (
                                                    <button
                                                        type="button"
                                                        onClick={() => stepPortion(idx, +1)}
                                                        aria-label={`increase portion of ${item.name}`}
                                                        data-testid={`breakdown-step-up-${idx}`}
                                                        className="w-6 h-6 flex items-center justify-center rounded border border-amber-300 dark:border-amber-700 bg-white/70 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-200 transition-colors flex-shrink-0"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Row 3: macro chips — each chip gets ¼ of the row → plenty of room for "30.5g" */}
                                            <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                                                <div className="px-1.5 py-1 rounded-md bg-orange-50 dark:bg-orange-950/30 border border-orange-200/60 dark:border-orange-800/40 flex items-center justify-between gap-0.5">
                                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-orange-700/80 dark:text-orange-300/80">kcal</span>
                                                    {editable ? (
                                                        <InlineCell value={Math.round(item.calories || 0)} idx={idx} field="calories" colorClass="text-orange-600 dark:text-orange-300 font-semibold" widthClass="w-10" />
                                                    ) : (
                                                        <span className="font-semibold text-orange-600 dark:text-orange-300">{Math.round(item.calories || 0)}</span>
                                                    )}
                                                </div>
                                                <div className="px-1.5 py-1 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-800/40 flex items-center justify-between gap-0.5">
                                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-red-700/80 dark:text-red-300/80">P</span>
                                                    {editable ? (
                                                        <InlineCell value={Number(item.protein || 0).toFixed(0)} idx={idx} field="protein" suffix="g" colorClass="text-red-500 dark:text-red-300" widthClass="w-8" />
                                                    ) : (
                                                        <span className="font-medium text-red-500 dark:text-red-300">{Number(item.protein || 0).toFixed(0)}g</span>
                                                    )}
                                                </div>
                                                <div className="px-1.5 py-1 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/40 flex items-center justify-between gap-0.5">
                                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80">K</span>
                                                    {editable ? (
                                                        <InlineCell value={Number(item.carbs || 0).toFixed(0)} idx={idx} field="carbs" suffix="g" colorClass="text-amber-600 dark:text-amber-300" widthClass="w-8" />
                                                    ) : (
                                                        <span className="font-medium text-amber-600 dark:text-amber-300">{Number(item.carbs || 0).toFixed(0)}g</span>
                                                    )}
                                                </div>
                                                <div className="px-1.5 py-1 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200/60 dark:border-yellow-800/40 flex items-center justify-between gap-0.5">
                                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-yellow-700/80 dark:text-yellow-300/80">F</span>
                                                    {editable ? (
                                                        <InlineCell value={Number(item.fat || 0).toFixed(0)} idx={idx} field="fat" suffix="g" colorClass="text-yellow-600 dark:text-yellow-300" widthClass="w-8" />
                                                    ) : (
                                                        <span className="font-medium text-yellow-600 dark:text-yellow-300">{Number(item.fat || 0).toFixed(0)}g</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Total Row — tappable chips: tap a macro → see how it breaks down per ingredient */}
                                    <div className="p-2.5 border-t-2 border-border bg-muted/40 space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-foreground">{t.total}</div>
                                            <div className="text-[9px] text-muted-foreground italic">{t.tapHint}</div>
                                        </div>
                                        <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                                            <button
                                                type="button"
                                                onClick={() => setExplainMacro('calories')}
                                                data-testid="breakdown-total-kcal-btn"
                                                className="px-1.5 py-1 rounded-md bg-orange-100/70 dark:bg-orange-950/50 border border-orange-300 dark:border-orange-700 flex items-center justify-between gap-0.5 hover:brightness-105 active:scale-[0.97] transition-all"
                                            >
                                                <span className="text-[9px] font-semibold uppercase tracking-wider text-orange-700 dark:text-orange-300">kcal</span>
                                                <span className="font-bold text-orange-700 dark:text-orange-200" data-testid="breakdown-total-calories">{totalsDisplay.calories}</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setExplainMacro('protein')}
                                                data-testid="breakdown-total-protein-btn"
                                                className="px-1.5 py-1 rounded-md bg-red-100/70 dark:bg-red-950/50 border border-red-300 dark:border-red-700 flex items-center justify-between gap-0.5 hover:brightness-105 active:scale-[0.97] transition-all"
                                            >
                                                <span className="text-[9px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">P</span>
                                                <span className="font-bold text-red-600 dark:text-red-200">{totalsDisplay.protein}g</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setExplainMacro('carbs')}
                                                data-testid="breakdown-total-carbs-btn"
                                                className="px-1.5 py-1 rounded-md bg-amber-100/70 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-700 flex items-center justify-between gap-0.5 hover:brightness-105 active:scale-[0.97] transition-all"
                                            >
                                                <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">K</span>
                                                <span className="font-bold text-amber-700 dark:text-amber-200">{totalsDisplay.carbs}g</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setExplainMacro('fat')}
                                                data-testid="breakdown-total-fat-btn"
                                                className="px-1.5 py-1 rounded-md bg-yellow-100/70 dark:bg-yellow-950/50 border border-yellow-300 dark:border-yellow-700 flex items-center justify-between gap-0.5 hover:brightness-105 active:scale-[0.97] transition-all"
                                            >
                                                <span className="text-[9px] font-semibold uppercase tracking-wider text-yellow-700 dark:text-yellow-300">F</span>
                                                <span className="font-bold text-yellow-700 dark:text-yellow-200">{totalsDisplay.fat}g</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Nährstoff-Highlight / Tip */}
                            {hasTip && (
                                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40">
                                    <p className="text-xs text-emerald-900 dark:text-emerald-100 leading-relaxed">
                                        <span className="font-bold">💡 {t.highlightLabel}: </span>
                                        {result.tip}
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tap-to-explain Sheet — opens when user taps a TOTAL macro chip */}
            <AnimatePresence>
                {explainMacro && hasBreakdown && (() => {
                    // Static class maps so Tailwind JIT can pick them up.
                    const COLOR_CLASSES = {
                        orange: {
                            badgeBg: 'bg-orange-100 dark:bg-orange-950/40',
                            badgeBorder: 'border-orange-300 dark:border-orange-700',
                            label: 'text-orange-700 dark:text-orange-300',
                            value: 'text-orange-700 dark:text-orange-200',
                            barTrack: 'bg-orange-100 dark:bg-orange-950/40',
                            barFill: 'bg-orange-500 dark:bg-orange-400',
                        },
                        red: {
                            badgeBg: 'bg-red-100 dark:bg-red-950/40',
                            badgeBorder: 'border-red-300 dark:border-red-700',
                            label: 'text-red-700 dark:text-red-300',
                            value: 'text-red-700 dark:text-red-200',
                            barTrack: 'bg-red-100 dark:bg-red-950/40',
                            barFill: 'bg-red-500 dark:bg-red-400',
                        },
                        amber: {
                            badgeBg: 'bg-amber-100 dark:bg-amber-950/40',
                            badgeBorder: 'border-amber-300 dark:border-amber-700',
                            label: 'text-amber-700 dark:text-amber-300',
                            value: 'text-amber-700 dark:text-amber-200',
                            barTrack: 'bg-amber-100 dark:bg-amber-950/40',
                            barFill: 'bg-amber-500 dark:bg-amber-400',
                        },
                        yellow: {
                            badgeBg: 'bg-yellow-100 dark:bg-yellow-950/40',
                            badgeBorder: 'border-yellow-300 dark:border-yellow-700',
                            label: 'text-yellow-700 dark:text-yellow-300',
                            value: 'text-yellow-700 dark:text-yellow-200',
                            barTrack: 'bg-yellow-100 dark:bg-yellow-950/40',
                            barFill: 'bg-yellow-500 dark:bg-yellow-400',
                        },
                    };
                    const macroMeta = {
                        calories: { field: 'calories', title: t.explainKcalTitle, unit: '', color: 'orange', total: totalsDisplay.calories },
                        protein:  { field: 'protein',  title: t.explainProteinTitle, unit: 'g', color: 'red',    total: `${totalsDisplay.protein}g` },
                        carbs:    { field: 'carbs',    title: t.explainCarbsTitle,   unit: 'g', color: 'amber',  total: `${totalsDisplay.carbs}g` },
                        fat:      { field: 'fat',      title: t.explainFatTitle,     unit: 'g', color: 'yellow', total: `${totalsDisplay.fat}g` },
                    }[explainMacro];
                    const cc = COLOR_CLASSES[macroMeta.color];
                    const totalNum = result.breakdown.reduce((s, it) => s + parseNum(it[macroMeta.field]), 0);
                    const rows = result.breakdown
                        .map((it) => ({
                            name: it.name,
                            portion: it.portion,
                            value: parseNum(it[macroMeta.field]),
                        }))
                        .filter((r) => r.value > 0)
                        .sort((a, b) => b.value - a.value);
                    return (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 bg-black/50 z-40"
                                onClick={() => setExplainMacro(null)}
                                data-testid="breakdown-explain-backdrop"
                            />
                            <motion.div
                                initial={{ y: '100%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '100%' }}
                                transition={{ type: 'spring', damping: 28, stiffness: 320 }}
                                className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[80vh] overflow-y-auto"
                                data-testid={`breakdown-explain-sheet-${explainMacro}`}
                            >
                                <div className="flex justify-center py-2">
                                    <div className="w-12 h-1.5 bg-muted-foreground/20 rounded-full" />
                                </div>
                                <div className="px-4 pb-6 space-y-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <h3 className="text-lg font-bold">{macroMeta.title}</h3>
                                        <button
                                            type="button"
                                            onClick={() => setExplainMacro(null)}
                                            aria-label={t.close}
                                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                                            data-testid="breakdown-explain-close"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Total badge */}
                                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${cc.badgeBg} border ${cc.badgeBorder}`}>
                                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${cc.label}`}>{t.total}</span>
                                        <span className={`text-base font-bold ${cc.value}`}>{macroMeta.total}</span>
                                    </div>

                                    {/* Per-ingredient breakdown bars */}
                                    <div className="space-y-1.5">
                                        {rows.map((r, i) => {
                                            const pct = totalNum > 0 ? Math.round((r.value / totalNum) * 100) : 0;
                                            return (
                                                <div key={`explain-${explainMacro}-${i}`} className="p-2 rounded-lg bg-muted/40 border border-border/60">
                                                    <div className="flex items-baseline justify-between gap-2 mb-1">
                                                        <span className="text-xs font-medium text-foreground truncate" title={r.name}>{r.name}</span>
                                                        <span className={`text-xs font-bold ${cc.label} shrink-0`}>
                                                            {macroMeta.field === 'calories' ? Math.round(r.value) : r.value.toFixed(1)}{macroMeta.unit}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`h-1.5 flex-1 rounded-full ${cc.barTrack} overflow-hidden`}>
                                                            <div className={`h-full ${cc.barFill} rounded-full`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="text-[10px] text-muted-foreground w-12 text-right shrink-0">
                                                            {pct}%
                                                        </span>
                                                    </div>
                                                    {r.portion && (
                                                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate" title={r.portion}>{r.portion}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Assumptions reminder */}
                                    {hasAssumptions && (
                                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-800/40">
                                            <Info className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
                                            <p className="text-[11px] text-blue-800 dark:text-blue-200 leading-relaxed">
                                                <span className="font-semibold">{t.portionsLabel}: </span>
                                                {result.assumptions}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </>
                    );
                })()}
            </AnimatePresence>
        </div>
    );
}
